import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  X, MapPin, Briefcase, GraduationCap, Building2,
  Award, Languages, Users, Star, Shield, Loader2, BookOpen,
  Trophy, Lightbulb, FileText, Activity, Calendar, ExternalLink, Mail, Github
} from 'lucide-react';

const Section = ({ icon: Icon, title, children }) => (
  <div className="mb-6 bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
    <div className="flex items-center gap-2 mb-4 border-b border-gray-50 pb-3">
      <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center">
        <Icon className="w-4 h-4 text-[#007BFF]" />
      </div>
      <h4 className="text-sm font-bold text-gray-800 uppercase tracking-wide">{title}</h4>
    </div>
    {children}
  </div>
);

const SkillChip = ({ skill, isMatched }) => {
  if (isMatched) {
    return (
      <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-[0_2px_10px_rgba(37,99,235,0.2)] border border-transparent hover:shadow-[0_4px_12px_rgba(37,99,235,0.3)] transform hover:-translate-y-0.5 transition-all cursor-default">
        {skill}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-white text-gray-700 shadow-sm border border-gray-200 hover:border-blue-300 hover:text-blue-600 hover:bg-blue-50/30 transform hover:-translate-y-0.5 transition-all cursor-default">
      {skill}
    </span>
  );
};

const formatDuration = (months) => {
  if (!months) return '';
  const y = Math.floor(months / 12);
  const m = months % 12;
  if (y > 0 && m > 0) return `${y}y ${m}m`;
  if (y > 0) return `${y}y`;
  return `${m}m`;
};

const formatMonthYear = (month, year) => {
  if (!year) return 'Present';
  if (!month) return `${year}`;
  const d = new Date(year, month - 1);
  return d.toLocaleString('default', { month: 'short', year: 'numeric' });
};

const ExpandableSummary = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  if (!text) return null;
  const words = text.split(/\s+/);
  const isLong = words.length > 80;

  if (!isLong) {
    return <p className="text-[14px] text-gray-700 leading-relaxed whitespace-pre-wrap">{text}</p>;
  }

  const displayText = expanded ? text : words.slice(0, 80).join(' ') + '...';

  return (
    <div>
      <p className="text-[14px] text-gray-700 leading-relaxed whitespace-pre-wrap inline">
        {displayText}
      </p>
      <button
        onClick={() => setExpanded(!expanded)}
        className="ml-2 text-[14px] font-semibold text-blue-600 hover:text-blue-800 transition-colors inline"
      >
        {expanded ? 'Read less' : 'Read more'}
      </button>
    </div>
  );
};

const ExperienceRole = ({ role }) => {
  const [expanded, setExpanded] = useState(false);
  const hasLongDesc = role.description && role.description.length > 180;

  return (
    <div className="relative pb-5 last:pb-0 pt-1">
      {/* Dot perfectly aligned with the center of the 3px border and middle of title text */}
      <div className="absolute -left-[27px] top-[9px] w-[11px] h-[11px] rounded-full bg-slate-300 border-[2px] border-white shadow-sm" />

      <h5 className="font-semibold text-gray-900 text-[14px] leading-snug">{role.position_title}</h5>

      <div className="text-gray-500 text-[13px] mt-0.5 font-medium">
        {formatMonthYear(role.date_from_month, role.date_from_year)} - {formatMonthYear(role.date_to_month, role.date_to_year)}
        {role.duration_months ? ` · ${formatDuration(role.duration_months)}` : ''}
      </div>

      {role.location && (
        <div className="text-gray-500 text-[13px] mt-0.5">{role.location}</div>
      )}

      {role.description && (
        <div className="mt-2.5">
          <p className={`text-[13.5px] text-gray-700 leading-relaxed whitespace-pre-wrap ${!expanded && hasLongDesc ? 'line-clamp-3' : ''}`}>
            {role.description}
          </p>
          {hasLongDesc && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-[#007BFF] text-xs font-semibold mt-1 hover:underline"
            >
              {expanded ? 'Show less' : 'Read more'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const CandidateDetailSidebar = ({ candidate, enrichedData, isEnriching, isOpen, onClose, searchFilters }) => {
  const [showAllSkills, setShowAllSkills] = useState(false);

  if (!candidate) return null;

  // Use enriched data if available, otherwise use lightweight candidate data
  const c = enrichedData || candidate;
  const isLightweight = !enrichedData;

  const combinedSkills = Array.from(new Set([
    ...(c.inferred_skills || []),
    ...(c.historical_skills || [])
  ])).filter(Boolean);

  // Normalize search skills to lowercase for robust matching
  const searchSkillSet = new Set((searchFilters?.skills || []).map(s => s.toLowerCase().trim()));
  const isSkillMatched = (skill) => searchSkillSet.has(skill.toLowerCase().trim());

  // Sort skills so matched ones appear first
  combinedSkills.sort((a, b) => {
    const matchA = isSkillMatched(a);
    const matchB = isSkillMatched(b);
    if (matchA && !matchB) return -1;
    if (!matchA && matchB) return 1;
    return 0;
  });

  const displayedSkills = showAllSkills ? combinedSkills : combinedSkills.slice(0, 20);

  // Group experience by company name
  const groupedExperience = c.experience?.reduce((acc, exp) => {
    const company = exp.company_name || 'Unknown Company';
    if (!acc[company]) {
      acc[company] = {
        company_name: company,
        company_logo_url: exp.company_logo_url,
        roles: [],
        total_duration_months: 0
      };
    }
    acc[company].roles.push(exp);
    if (exp.duration_months) {
      acc[company].total_duration_months += exp.duration_months;
    }
    return acc;
  }, {});

  const experienceGroups = groupedExperience ? Object.values(groupedExperience) : [];

  const emails = c.professional_emails_collection?.map(e => e.professional_email).filter(Boolean) || [];
  if (c.primary_professional_email && !emails.includes(c.primary_professional_email)) {
    emails.unshift(c.primary_professional_email);
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/30 z-40 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Centered Modal panel */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, x: '-50%', y: '-48%' }}
            animate={{ opacity: 1, scale: 1, x: '-50%', y: '-50%' }}
            exit={{ opacity: 0, scale: 0.95, x: '-50%', y: '-48%' }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="fixed left-1/2 top-1/2 h-[85vh] w-[95vw] sm:w-[650px] md:w-[750px] bg-[#F8FAFC] z-50 shadow-2xl flex flex-col rounded-2xl overflow-hidden border border-gray-200"
          >
            {/* Header - Juicebox Style */}
            <div className="flex-shrink-0 px-8 py-8 border-b border-gray-200 bg-white relative">
              
              {/* Top Right Controls (Social, Close) */}
              <div className="absolute right-6 top-6 flex items-center gap-2">
                {(c.github_url || candidate.github_url) && (
                  <a href={c.github_url || candidate.github_url} target="_blank" rel="noopener noreferrer" className="p-1.5 text-[#24292e] bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
                    <Github className="w-5 h-5" />
                  </a>
                )}
                 {(c.linkedin_url || candidate.linkedin_url) && (
                  <a href={c.linkedin_url || candidate.linkedin_url} target="_blank" rel="noopener noreferrer" className="p-1.5 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors" title="View Professional Profile">
                    <ExternalLink className="w-5 h-5" />
                  </a>
                )}
                <button onClick={onClose} className="p-1.5 rounded-xl hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors ml-1">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex items-start gap-5 pr-32">
                {/* Left Section: Picture, Name, Location, Badges */}
                {c.picture_url ? (
                  <img src={c.picture_url} alt={candidate.full_name} className="w-16 h-16 rounded-2xl object-cover shadow-sm border border-gray-100 shrink-0" />
                ) : (
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#007BFF] to-[#8472B3] flex items-center justify-center text-white text-xl font-bold shadow-md shrink-0">
                    {(candidate.full_name || '?').charAt(0)}
                  </div>
                )}

                <div className="flex flex-col gap-1 min-w-0 w-full">
                  <h2 className="text-2xl font-bold text-gray-900 tracking-tight truncate">{candidate.full_name}</h2>
                  
                  <p className="text-[15px] text-gray-500 font-medium truncate">
                    {candidate.location_full || 'Location not specified'}
                  </p>

                  {/* Badges Row */}
                  <div className="flex flex-nowrap items-center gap-2.5 mt-2 w-full">
                    {experienceGroups[0] && (
                      <div className="flex items-center gap-2 text-[13px] text-gray-800 font-medium bg-gray-50 px-2 py-1 rounded-md border border-gray-100 shrink min-w-0">
                        {experienceGroups[0].company_logo_url ? (
                          <img src={experienceGroups[0].company_logo_url} className="w-5 h-5 rounded bg-white object-cover border border-gray-200 shrink-0" alt="" />
                        ) : (
                          <div className="w-5 h-5 rounded bg-white flex items-center justify-center border border-gray-200 shrink-0"><Building2 className="w-3 h-3 text-slate-400" /></div>
                        )}
                        <span className="truncate">{experienceGroups[0].company_name}</span>
                      </div>
                    )}

                    {c.education?.[0] && (
                      <div className="flex items-center gap-2 text-[13px] text-gray-800 font-medium bg-gray-50 px-2 py-1 rounded-md border border-gray-100 shrink min-w-0">
                        {c.education[0].institution_logo_url ? (
                          <img src={c.education[0].institution_logo_url} className="w-5 h-5 rounded-full bg-white object-contain border border-gray-200 shrink-0" alt="" />
                        ) : (
                          <div className="w-5 h-5 rounded-full bg-white flex items-center justify-center border border-gray-200 shrink-0"><GraduationCap className="w-3 h-3 text-slate-400" /></div>
                        )}
                        <span className="truncate">{c.education[0].institution_name}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Content Body */}
            <div className="flex-1 overflow-y-auto p-6 scrollbar-thin">
              {isEnriching ? (
                <div className="flex flex-col items-center justify-center h-full py-20 gap-4">
                  <div className="relative">
                    <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center border border-blue-100">
                      <Loader2 className="w-8 h-8 text-[#007BFF] animate-spin" />
                    </div>
                    <motion.div className="absolute inset-0 rounded-2xl" animate={{ boxShadow: ['0 0 0 0 rgba(0,123,255,0.2)', '0 0 0 15px rgba(0,123,255,0)'] }} transition={{ duration: 1.5, repeat: Infinity }} />
                  </div>
                  <div className="text-center">
                    <p className="text-base text-gray-800 font-bold mb-1">Enriching Profile</p>
                    <p className="text-sm text-gray-500">Unlocking full history, skills, and activities...</p>
                  </div>
                </div>
              ) : isLightweight ? (
                <div className="flex flex-col items-center justify-center h-full py-20 gap-3 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-2">
                    <Shield className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-base text-gray-800 font-bold">Details Unavailable</p>
                  <p className="text-sm text-gray-500 max-w-xs">Could not load the full profile details right now. Try clicking the candidate again.</p>
                </div>
              ) : (
                <div className="space-y-6">

                  {/* Summary */}
                  {c.summary && (
                    <Section icon={FileText} title="Professional Summary">
                      <ExpandableSummary text={c.summary} />
                    </Section>
                  )}

                  {/* Skills */}
                  {combinedSkills.length > 0 && (
                    <Section icon={Lightbulb} title={`Skills (${combinedSkills.length})`}>
                      <div className="flex flex-wrap gap-2.5">
                        {displayedSkills.map((skill, i) => (
                          <SkillChip key={i} skill={skill} isMatched={isSkillMatched(skill)} />
                        ))}
                      </div>
                      {combinedSkills.length > 20 && (
                        <div className="mt-4 flex justify-center">
                          <button
                            onClick={() => setShowAllSkills(!showAllSkills)}
                            className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors duration-200"
                          >
                            {showAllSkills ? 'Show Less' : `Show ${combinedSkills.length - 20} More Skills`}
                          </button>
                        </div>
                      )}
                    </Section>
                  )}

                  {/* Experience */}
                  {experienceGroups.length > 0 && (
                    <Section icon={Briefcase} title={`Experience · ${formatDuration(c.total_experience_duration_months) || ''} total`}>
                      <div className="space-y-0">
                        {experienceGroups.map((group, gi) => (
                          <div key={gi}>
                            {/* Company Header Row */}
                            <div className="flex items-center gap-3 py-3">
                              {group.company_logo_url ? (
                                <img
                                  src={group.company_logo_url}
                                  alt={group.company_name}
                                  className="w-10 h-10 rounded-full object-cover bg-gray-100 border border-gray-200 shrink-0"
                                  onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                                />
                              ) : null}
                              <div className="w-10 h-10 rounded-full bg-slate-100 border border-slate-200 items-center justify-center shrink-0" style={{ display: group.company_logo_url ? 'none' : 'flex' }}>
                                <Building2 className="w-5 h-5 text-slate-400" />
                              </div>
                              <div className="min-w-0">
                                <h4 className="font-bold text-gray-900 text-[15px] leading-tight truncate">{group.company_name}</h4>
                                <span className="text-gray-500 text-xs">{formatDuration(group.total_duration_months)}</span>
                              </div>
                            </div>

                            {/* Roles under this company */}
                            <div className="ml-5 border-l-[3px] border-gray-200 pl-5 pb-2">
                              {group.roles.map((role, ri) => (
                                <ExperienceRole key={ri} role={role} />
                              ))}
                            </div>

                            {/* Separator between companies */}
                            {gi < experienceGroups.length - 1 && <div className="border-b border-gray-100 my-1" />}
                          </div>
                        ))}
                      </div>
                    </Section>
                  )}

                  {/* Education */}
                  {c.education?.length > 0 && (
                    <Section icon={GraduationCap} title="Education">
                      <div className="space-y-0">
                        {c.education.map((edu, i) => (
                          <div key={i}>
                            <div className="flex items-start gap-4 py-3">
                              {edu.institution_logo_url ? (
                                <img
                                  src={edu.institution_logo_url}
                                  alt={edu.institution_name}
                                  className="w-10 h-10 rounded-full object-cover bg-gray-50 border border-gray-200 shrink-0 mt-0.5"
                                  onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                                />
                              ) : null}
                              <div className="w-10 h-10 rounded-full bg-slate-100 border border-slate-200 items-center justify-center shrink-0 mt-0.5" style={{ display: edu.institution_logo_url ? 'none' : 'flex' }}>
                                <BookOpen className="w-5 h-5 text-slate-400" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <h5 className="text-[14px] font-bold text-gray-900 leading-snug">{edu.institution_name}</h5>
                                {edu.degree && <p className="text-[13px] text-gray-500 mt-0.5">{edu.degree}</p>}
                                {(edu.date_from_year || edu.date_to_year) && (
                                  <p className="text-[12px] text-gray-400 mt-0.5">
                                    {formatMonthYear(edu.date_from_month, edu.date_from_year)} – {formatMonthYear(edu.date_to_month, edu.date_to_year)}
                                  </p>
                                )}
                                {edu.description && <p className="text-[13px] text-gray-600 mt-2 leading-relaxed whitespace-pre-wrap">{edu.description}</p>}
                              </div>
                            </div>
                            {/* Separator between education entries */}
                            {i < c.education.length - 1 && <div className="border-b border-gray-100 my-1" />}
                          </div>
                        ))}
                      </div>
                    </Section>
                  )}

                  {/* Certifications */}
                  {c.certifications?.length > 0 && (
                    <Section icon={Award} title="Certifications">
                      <div className="space-y-0">
                        {c.certifications.map((cert, i) => (
                          <div key={i}>
                            <div className="flex items-start gap-4 py-3">
                              {cert.certificate_logo_url ? (
                                <img
                                  src={cert.certificate_logo_url}
                                  alt={cert.issuer}
                                  className="w-10 h-10 rounded-full object-cover bg-gray-50 border border-gray-200 shrink-0 mt-0.5"
                                  onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                                />
                              ) : null}
                              <div className="w-10 h-10 rounded-full bg-slate-100 border border-slate-200 items-center justify-center shrink-0 mt-0.5" style={{ display: cert.certificate_logo_url ? 'none' : 'flex' }}>
                                <Award className="w-5 h-5 text-slate-400" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <h5 className="text-[14px] font-bold text-gray-900 leading-snug">{cert.title}</h5>
                                {cert.issuer && <p className="text-[13px] text-gray-500 mt-0.5">{cert.issuer}</p>}
                                {(cert.date_from_year || cert.date_from_month) && (
                                  <p className="text-[12px] text-gray-400 mt-0.5">
                                    Issued {formatMonthYear(cert.date_from_month, cert.date_from_year)}
                                  </p>
                                )}
                              </div>
                              {cert.certificate_url && (
                                <a href={cert.certificate_url} target="_blank" rel="noopener noreferrer" className="shrink-0 p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                                  <ExternalLink className="w-4 h-4" />
                                </a>
                              )}
                            </div>
                            {i < c.certifications.length - 1 && <div className="border-b border-gray-100 my-1" />}
                          </div>
                        ))}
                      </div>
                    </Section>
                  )}

                  {/* Awards */}
                  {c.awards?.length > 0 && (
                    <Section icon={Trophy} title="Awards">
                      <div className="space-y-0">
                        {c.awards.map((award, i) => (
                          <div key={i}>
                            <div className="flex items-start gap-4 py-3">
                              <div className="w-10 h-10 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0 mt-0.5">
                                <Trophy className="w-5 h-5 text-slate-400" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <h5 className="text-[14px] font-bold text-gray-900 leading-snug">{award.title}</h5>
                                {award.issuer && <p className="text-[13px] text-gray-500 mt-0.5">{award.issuer}</p>}
                                {award.date_year && (
                                  <p className="text-[12px] text-gray-400 mt-0.5">{award.date_year}</p>
                                )}
                              </div>
                            </div>
                            {i < c.awards.length - 1 && <div className="border-b border-gray-100 my-1" />}
                          </div>
                        ))}
                      </div>
                    </Section>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CandidateDetailSidebar;
